import { useSearchParams, Navigate, Link } from 'react-router-dom'
import { CheckCircle, Confetti, Storefront, WarningCircle } from '@phosphor-icons/react'
import { parseISO } from 'date-fns/esm'

import { formatPhoneNumber } from '@/cart/utils/formatPhoneNumber'

import { useAppSelector } from '@/main/app/hooks/storeHooks'
import { useListOutletContext } from '@/cart/hooks/useListOutletContext'
import { useAppContext } from '@/main/app/context'
import { DeliveryValues } from '../DeliveryValues/DeliveryValues'

// const formatShortDate = (date?: Date | number) =>
//   new Intl.DateTimeFormat('default', {
//     month: 'numeric',
//     day: 'numeric',
//   }).format(date)

const formatLongDate = (date?: Date | number) =>
  new Intl.DateTimeFormat('default', {
    dateStyle: 'full',
  }).format(date)

export function Success() {
  const {
    formPaths: [step1, , step3],
  } = useAppContext()
  const { id, list } = useListOutletContext()
  const [search] = useSearchParams()
  const appHash = useAppSelector((state) => state.app.successHash)
  const searchHash = search.get('id')
  const optedIn = search.get('optIn')

  if (!appHash || appHash !== searchHash) {
    return <Navigate to="/" />
  }

  const startDate = list.history && parseISO(list.history.dates.startDate)
  const endDate = list.history && parseISO(list.history.dates.endDate)

  return (
    <div className="prose-h3:first-of-type:twc-mt-0">
      {optedIn !== 'no' && (
        <div
          className={`twc-rounded twc-gap-2 twc-px-4 twc-py-4 twc-flex twc-items-start twc-mb-4 ${
            optedIn === 'yes'
              ? 'twc-bg-sky-100 dark:twc-bg-sky-900/50'
              : 'twc-bg-amber-100 dark:twc-bg-stone-600/50'
          }`}
        >
          {optedIn === 'yes' ? (
            <Confetti size={20} className="twc-text-sky-800 dark:twc-text-sky-100" />
          ) : (
            <WarningCircle size={20} className="twc-text-amber-800 dark:twc-text-amber-100" />
          )}
          <p className="twc-my-0 twc-text-sm/normal">
            {optedIn === 'yes' ? (
              <>Thanks for signing up for the ACME Props Newsletter!</>
            ) : (
              <>
                There was a problem subscribing to the ACME Props Newsletter.{' '}
                <a href="http://eepurl.com/it5vsg">Use this link to sign up directly.</a>
              </>
            )}
          </p>
        </div>
      )}
      <div className="twc-rounded twc-shadow-lg twc-bg-emerald-100 dark:twc-bg-emerald-900/50 twc-px-8 twc-py-6 twc-flex twc-flex-col twc-items-center twc-justify-center twc-mb-8">
        <CheckCircle size={40} weight="fill" className="twc-text-emerald-800 twc-mb-4" />
        <h2>&quot;{list.name}&quot; was submitted to ACME!</h2>
        <p>
          <strong>Your request is being quoted.</strong> We will check availability of the props in
          your cart for your rental dates. We will be in touch via e-mail shortly thereafter.
        </p>
      </div>

      <p>
        <strong>Need to make changes?</strong> You can update your request as many times as needed.
        Update props in your cart, <Link to={`/${id}/form/${step1}`}>edit your request info</Link>,
        and <Link to={`/${id}/form/${step3}`}>re-submit the form</Link> to get an updated quote.
      </p>
      <p>
        <strong>Office hours are 9am to 6pm, Monday thru Friday.</strong> Submissions outside of
        these hours will be responded to the next business day.
      </p>

      {list.history && (
        <div className="twc-rounded twc-shadow twc-bg-slate-50 dark:twc-bg-slate-800/50 twc-p-8 twc-mt-8">
          <h3 className="twc-text-xl">Your submission</h3>
          <p>Sent {new Intl.DateTimeFormat().format(Date.parse(list.history.sent))}</p>
          <p>
            <em>
              This is not an order confirmation or receipt. You will receive availability and a
              quote from us via e-mail.
            </em>
          </p>
          <h4 className="twc-text-lg">Contact and Billing</h4>
          <dl>
            <div className="twc-flex twc-gap-1">
              <dt className="twc-font-semibold">Contact:</dt>
              <dd>
                {list.history.profile.firstName} {list.history.profile.lastName}
              </dd>
            </div>

            <div className="twc-flex twc-gap-1">
              <dt className="twc-font-semibold">Bill To:</dt>
              <dd>{list.history.billing.client}</dd>
            </div>

            <div className="twc-flex twc-gap-1">
              <dt className="twc-font-semibold">Email:</dt>
              <dd>{list.history.profile.email}</dd>
            </div>

            <div className="twc-flex twc-gap-1">
              <dt className="twc-font-semibold">Phone:</dt>
              <dd>{formatPhoneNumber(list.history.profile.phone)}</dd>
            </div>

            <div className="twc-flex twc-gap-1">
              <dt className="twc-font-semibold">Reference:</dt>
              <dd>{list.history.billing.reference}</dd>
            </div>

            {list.history.billing.cc && (
              <div className="twc-flex twc-gap-1">
                <dt className="twc-font-semibold">Additional Email (CC):</dt>
                <dd>{list.history.billing.cc}</dd>
              </div>
            )}
          </dl>

          <h4 className="twc-text-lg">
            Rental Dates &amp;{' '}
            {list.history.shipping.shippingMethod === 'delivery' ? 'Delivery' : 'Pick-up'}
          </h4>

          <p>
            <strong className="twc-font-semibold">
              {list.history.shipping.shippingMethod === 'delivery' ? 'Delivery' : 'Pick-up'}:
            </strong>{' '}
            {formatLongDate(startDate)}
            <br />
            <strong className="twc-font-semibold">Return:</strong> {formatLongDate(endDate)}
          </p>

          {list.history.shipping.shippingMethod === 'pickup' ? (
            <p>
              <strong>Pick-up at ACME&apos;s location</strong> <Storefront className="twc-inline" />
              <br />
              <a
                className="twc-text-blue-600 twc-underline hover:twc-text-brand hover:twc-no-underline"
                href="https://g.page/AcmeBrooklyn?share"
              >
                350 Meserole St., Building D, Brooklyn, NY 11206
              </a>
              <br />
              <strong className="twc-font-semibold">Hours:</strong> 9 AM to 6 PM
              <br />
              Between Bogart S. &amp; Morgan Ave in Bushwick
            </p>
          ) : list.history.shipping.shippingMethod === 'delivery' &&
            list.history.shipping.shippingDetail ? (
            <DeliveryValues values={list.history.shipping.shippingDetail} />
          ) : null}
        </div>
      )}
    </div>
  )
}
